import React, {useMemo, useEffect, useRef, useState, useContext} from 'react';
import {Button, ErrorBoundary, Flex} from 'components';
import {createPortal} from 'react-dom';
import {Context} from './context';
import {noop, useEvent, useClickAway, useWindowSize, memoizeOne, mapValues, addPx, useRafState, pick, useToggle} from 'helpers';
import css from './popup.module.scss';

export function Card(popup) {
  const [history] = useState([]);
  const ref = useRef();
  const [transition, setTransition] = useRafState('');
  const [{id, target, container, style, className, align, Component, props, type, detached}, setState] = useState(create(popup));
  const [customClassName, setCustomClassName] = useRafState();
  const lock = useRef(false);

  const controls = useMemo(() => ({
    id,
    setClassName: memoizeOne(className => {
      setCustomClassName(className.split(' '));
    }),
    close() {
      if (!lock.current) {
        window.dispatchEvent(new CustomEvent('popupclose', {detail: popup}));
      }
    },
    bounce() {
      setTransition('bounce');
      setTimeout(() => setTransition(''), 200);
    },
    detach() {
      setState(state => ({...state, detached: true}));
    },
    redirect: (Component, props, push = true) => event => {
      event?.stopPropagation?.();
      controls.bounce();
      window.setTimeout(() => setState(state => {
        if (push) {
          history.push(pick(state, 'Component', 'props'));
        }
        return {
          ...state,
          Component,
          props: {...state.props, ...props}
        };
      }), 100);
    },
    back(event) {
      const previous = history.pop();
      if (previous) {
        controls.redirect(previous.Component, previous.props, false)(event);
      } else {
        controls.close();
      }
    }
  }), []);

  // popup behaviour when focus is lost
  if (/suggestion|focus/.test(type)) {
    useEvent('focusout', detached ? noop : closeMaybe, target);
    useClickAway(ref, detached ? controls.close : noop);
    if (props.input) {
      useEvent('input', () => setState(state => ({...state})), props.input.current);
    }
  } else if (popup.notification) {
    // if another notification popups up - close this one
    useEvent('popupopen', ({detail}) => {
      if (detail.notification) {
        controls.close();
      }
    });
    useEffect(() => {
      const timer = window.setTimeout(controls.close, popup.error ? 7000 : 3000);
      return () => window.clearTimeout(timer);
    }, []);
  } else if (props.dismissible !== false) {
    useClickAway(ref, transition === 'bounce' ? noop : controls.close);
  }

  // exiting transition (for animation)
  useEvent('popupclose', ({detail}) => {
    if (detail.id === id && transition !== 'exited') {
      setTransition('exiting');
    }
  });

  // listen to props changes (if deps specified)
  useEffect(() => {
    if (props.deps) {
      const sync = ({detail}) => setState(state => ({...state, props: detail}));
      window.addEventListener('popupsync-' + id, sync);
      return () => window.removeEventListener('popupsync-' + id, sync);
    }
  }, []);

  // update the target - indicate that it has popup opened
  useEffect(() => {
    if (target) {
      target.setAttribute('aria-expanded', 'true');
      return () => target.removeAttribute('aria-expanded');
    }
  }, []);

  // if inside the scrollable widget - hide the popup on scroll
  useEffect(() => {
    const widget = target?.closest('[role="feed"]');
    if (widget) {
      window.addEventListener('scroll', controls.close);
      widget.addEventListener('scroll', controls.close);
      return () => {
        window.removeEventListener('scroll', controls.close);
        widget.removeEventListener('scroll', controls.close);
      };
    }
  }, []);

  // align the popup after it's mounted (or if window size changes)
  useEffect(() => {
    requestAnimationFrame(() => setState(align));
    setTransition(transition => style ? transition : 'entering');
  }, [useWindowSize()]);

  useEvent('REDUX_UI_POPUP_REDIRECT', (event) => {
    if (event.detail.Component) {
      lock.current = true;
      requestAnimationFrame(() => {
        lock.current = false;
        controls.redirect(event.detail.Component)(event);
      });
    }
  });

  const attributes = {
    ref,
    className: css('popup', className, customClassName, transition),
    style,
    onPointerDown: target?.tagName === 'DIV' && /suggestion|focus/.test(type) ? controls.detach : noop
  };

  return createPortal(
    <div {...attributes} aria-roledescription={popup.modal ? 'modal' : 'popup'}>
      <Context.Provider value={controls}>
        <ErrorBoundary wrap={ErrorMessage}>
          <Component {...props} popup={controls} modal={popup.modal ? controls : undefined} />
        </ErrorBoundary>
        {className['has-arrow'] &&
          <div className={css('arrow', className)} />
        }
      </Context.Provider>
    </div>,
    container
  );
}

function create(popup) {
  const target = window.document.getElementById(popup.id);
  return {
    align,
    container: target?.closest(`label, body, .popup-container, .${css.popup}`) ?? window.document.getElementById('outsider'),
    target,
    className: {beforeEnter: true},
    ...popup
  };
}

function align(popup) {
  const scrolled = popup.container === window.document.body ? 0 : popup.container.scrollTop;
  const target = popup.target?.getBoundingClientRect();
  const container = popup.container.getBoundingClientRect();
  const center = window.innerWidth / 2;
  const className = {
    ...popup.className,
    'beforeEnter': false,
    'has-arrow': popup.target?.tagName === 'svg',
    'right-to-left': (target?.left + target?.width / 2) >= center,
    'left-to-right': (target?.left + target?.width / 2) < center,
    'bottom-to-top': target?.bottom / window.innerHeight > 0.65,
    [popup.props.className]: true
  };
  const arrowCompensation = className['has-arrow'] ? target?.width + 12 : 0;
  const style = {
    ...popup.style,
    top: target?.bottom - container.top + scrolled,
    left: target?.left + arrowCompensation - container.left,
    width: className.adjacent ? target?.width : 'auto',
    '--available-height': window.innerHeight - target?.bottom - 64
  };
  if (className['right-to-left']) {
    style.left = target[className['has-arrow'] ? 'left' : 'right'] - container.left;
  }
  if (className['bottom-to-top']) {
    style.top = target?.top - container.top + scrolled;
    style['--available-height'] = target?.top - 128;
  }
  return {
    ...popup,
    className,
    style: mapValues(style, addPx)
  };
}

function closeMaybe({currentTarget, relatedTarget}) {
  const id = currentTarget.closest('[aria-haspopup]')?.id;
  if (!relatedTarget || !relatedTarget.closest(`[id="${id}"]`)) {
    window.dispatchEvent(new CustomEvent('popupclose', {
      detail: {id}
    }));
  }
}

const ErrorMessage = (content) => {
  const {close} = useContext(Context);
  return (
    <div>
      {content}
      <Flex className={['right', 'spacing']}>
        <Button onClick={close}>Close</Button>
      </Flex>
    </div>
  );
};
